import cn from 'classnames'
import BaseFlyout from 'ext-ui/components/BaseFlyout/BaseFlyout'
import React, { ReactElement, ReactNode } from 'react'
import SvgTooltip from 'src/components/Svgs/SvgTooltip'

import style, { flyoutStyle } from './SettingsDrawerShell.style'

type SettingsDrawerShellProps = {
  isOpen: boolean
  onClose: () => void
  /** Also doubles as the flyout's accessible name. */
  title: string
  lead: string
  /** Closing ⓘ note. Omitted entirely when a drawer has none. */
  footnote?: ReactNode
  children: ReactNode
  className?: string
}

/**
 * Shared shell for the Controls & Settings flyout drawers (EX-40632 —
 * "controls move into flyouts"). Factors out the shape
 * `SpendRestrictionsTile`'s two existing drawers (Geofence, Merchant
 * categories) already established: content-owned heading, a lead paragraph,
 * hairline-divided sections, and a closing note — so each new drawer supplies
 * only its own body content instead of re-implementing the wrapper.
 *
 * Sections inside `children` that need the hairline divider should use
 * `SettingsDrawerSection` below.
 */
export default function SettingsDrawerShell(
  props: SettingsDrawerShellProps,
): ReactElement {
  const { isOpen, onClose, title, lead, footnote, children, className } = props

  return (
    <BaseFlyout
      isOpen={isOpen}
      onClose={onClose}
      className={cn('SettingsDrawerShell', className)}
      css={flyoutStyle}
      aria-label={title}
    >
      <div className="SettingsDrawerShell-body" css={style}>
        <div className="SettingsDrawerShell-heading">
          <h2>{title}</h2>
          <p className="SettingsDrawerShell-lead">{lead}</p>
        </div>
        {children}
        {footnote !== undefined && (
          <p className="SettingsDrawerShell-footnote">
            <SvgTooltip
              width={14}
              height={14}
              viewBox="0 0 24 24"
              aria-hidden="true"
            />
            {footnote}
          </p>
        )}
      </div>
    </BaseFlyout>
  )
}

/** Hairline-divided section wrapper for drawer body content. */
export function SettingsDrawerSection(props: {
  children: ReactNode
}): ReactElement {
  return <div className="SettingsDrawerShell-section">{props.children}</div>
}
